import { buildDashboardDocument } from '@/lib/dashRuntime';
import { getDashboard } from '@/lib/dashboards-api';
import { cn } from '@/lib/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';

import { useTheme } from '@/components/ThemeProvider';

const PAGE_WIDTH = 1100;
const PAGE_HEIGHT = 700;
const DEFAULT_PREVIEW_WIDTH = 360;

export default function DashboardPreview({
    dashboardId,
    width,
    html,
    className
}: {
    dashboardId: string;
    width?: number;
    html?: string;
    className?: string;
}) {
    const { variant } = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const [measuredWidth, setMeasuredWidth] = useState(DEFAULT_PREVIEW_WIDTH);

    useEffect(() => {
        const container = containerRef.current;
        if (width !== undefined || !container) return;
        setMeasuredWidth(container.clientWidth || DEFAULT_PREVIEW_WIDTH);
        const observer = new ResizeObserver(([entry]) => {
            const next = Math.round(entry.contentRect.width);
            setMeasuredWidth((previous) =>
                previous === next ? previous : next
            );
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [width]);

    const previewWidth = width ?? measuredWidth;
    const scale = previewWidth / PAGE_WIDTH;

    const { data: dashboard } = useSWR(
        html === undefined ? ['dashboard', dashboardId] : null,
        ([, id]) => getDashboard(id),
        { revalidateOnFocus: false, revalidateOnReconnect: false }
    );

    const sourceHtml = html ?? dashboard?.html;

    const documentHtml = useMemo(
        () =>
            sourceHtml !== undefined
                ? buildDashboardDocument(sourceHtml, variant).replace(
                      '</head>',
                      '<style>html{scrollbar-width:none}</style></head>'
                  )
                : null,
        [sourceHtml, variant]
    );

    return (
        <div
            ref={containerRef}
            className={cn(
                'relative bg-background shadow-md border rounded-xl overflow-hidden',
                className
            )}
            style={
                width === undefined
                    ? {
                          width: '100%',
                          aspectRatio: `${PAGE_WIDTH} / ${PAGE_HEIGHT}`
                      }
                    : { width, height: PAGE_HEIGHT * scale }
            }
        >
            {documentHtml ? (
                <iframe
                    srcDoc={documentHtml}
                    sandbox="allow-scripts"
                    title={dashboard?.title}
                    tabIndex={-1}
                    className="top-0 left-0 absolute border-0 pointer-events-none origin-top-left"
                    style={{
                        width: PAGE_WIDTH,
                        height: PAGE_HEIGHT,
                        transform: `scale(${scale})`
                    }}
                />
            ) : (
                <p className="p-4 text-muted-foreground text-sm">
                    Loading preview…
                </p>
            )}
        </div>
    );
}
